/**
 * Copyright ©2022 Drivepoint
 */

import React from "react";
import {Route, Routes} from "react-router-dom";
import usePathContext from "@hooks/usePathContext";
import useRoutes from "@hooks/useRoutes";
import useStateChange from "@hooks/useStateChange";
import {FirebaseAuthenticationState} from "@services/firebase/Firebase";
import ServiceRegistry from "@services/ServiceRegistry";
import DrivepointUser from "@services/user/DrivepointUser";
import DefaultPage from "../../pages/DefaultPage";
import LoadingPage from "../../pages/LoadingPage/LoadingPage";
import NotFoundPage from "../../pages/NotFoundPage";
import SignInPage from "../../pages/user/SignInPage/SignInPage";
import "./Routing.css";

export interface PageRoute {
  component?: React.ComponentType<any>;
  favorite: boolean;
  hidden: boolean;
  id: string;
  name: string;
  path: string;
  parent: string;
  pattern: RegExp;
  tokens: string[];
  visibility: string;
  icon?: React.ReactNode | null;
  external?: boolean;
  urlKey?: string;
  legacyUrlKey?: string;
  infoComponent?: React.ComponentType<any> | null;
  public?: boolean;
}

export default function Routing(props: any): JSX.Element {

  const routes = useRoutes();
  const user = useStateChange<DrivepointUser>("user");
  const authentication = useStateChange<FirebaseAuthenticationState>("authentication");
  const pathContext = usePathContext();

  function renderPageRoute(page: PageRoute, index: number): any {
    if (page.path && page.component) {
      return <Route key={`route_${index}`} path={page.path} element={<page.component page={page} />} />;
    }
  }

  function renderPageRoutes(): any {
    return routes.pages?.map((page: PageRoute, index: number) => {
      if (authentication === FirebaseAuthenticationState.INITIALIZING) {
        return <Route key={`route_${index}`} path={page.path} element={<LoadingPage />} />;
      }
      if (page.public) { return renderPageRoute(page, index); }
      if (!user) { return <Route key={`route_${index}`} path={page.path} element={<SignInPage />} />; }
      if (!pathContext.companyId) { return renderPageRoute(page, index); }
      const company = user.companies.find((it: any) => it.id === pathContext.companyId);
      if (company) { return renderPageRoute(page, index); }
    });
  }

  function renderIndexPage(): any {
    if (authentication === FirebaseAuthenticationState.INITIALIZING) {
      return <Route key="route_index" index element={<LoadingPage />} />;
    }
    if (!user) {
      const page = ServiceRegistry.routeService.getPageById("sign_in");
      if (page) {
        return <Route key="route_index" index element={<page.component page={page} />} />;
      }
    }
    return <Route key="route_index" index element={<DefaultPage />} />;
  }

  function renderWildcardRoute(): any {
    // if services are still initializing, show loading page
    if (authentication === FirebaseAuthenticationState.INITIALIZING) {
      return <Route key="route_wildcard" path="*" element={<LoadingPage />} />;
    }
    // if user is authenticated, it's safe to say the given page isn't found
    if (authentication === FirebaseAuthenticationState.AUTHENTICATED) {
      return <Route key="route_wildcard" path="*" element={<NotFoundPage />} />;
    }
    // Routes have not populated yet on this render — the specific matching page (e.g.
    // /quickstart) will render on the next render once `routes.pages` is set. Rendering
    // SignInPage here transiently mounts a second `BainbridgeAuthentication` before the
    // real page mounts its own, and both call `Firebase.ui.start()` on the same singleton
    // — the second call resets FirebaseUI mid-flight, dropping the pending Google redirect
    // result. Show LoadingPage instead until routes are ready.
    if (!routes.pages) {
      return <Route key="route_wildcard" path="*" element={<LoadingPage />} />;
    }
    // if user isn't authenticated, have them sign in
    return <Route key="route_wildcard" path="*" element={<SignInPage />} />;
  }

  function renderRoutes(): any {
    return <Routes>
      <>
        {renderIndexPage()}
        {renderPageRoutes()}
        {renderWildcardRoute()}
      </>
    </Routes>;
  }

  return <article className="routing">
    {renderRoutes()}
  </article>;

}
